/**
 * GhostRaceMap
 *
 * Leaflet map showing two animated dots moving along GPS tracks in sync
 * with the Ghost Race scrubber (scrubberTime = elapsed race seconds).
 *
 * Features:
 *   - Track polylines for A (blue) and B (amber)
 *   - Two animated CircleMarkers (dots) updated each frame
 *   - Fading trail (last 20 positions) per dot
 *   - Freeze indicator (pulse class) when dot is stopped
 *   - Gap HUD overlay in the top-right corner
 */

import { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';
const TRAIL_LEN = 20;

// Fix default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create a custom checkpoint icon (timer marker)
const createCheckpointIcon = () => {
  return L.divIcon({
    className: 'checkpoint-marker',
    html: `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
        border: 2px solid #fff;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
          <circle cx="12" cy="12" r="9"/>
          <polyline points="12 6 12 12 15 15"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

function formatHms(seconds) {
  if (!seconds) return '0:00:00';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
}

function formatKm(m) {
  if (m == null) return '—';
  return `${(m / 1000).toFixed(1)} km`;
}

function formatTimeDelta(s) {
  if (s == null) return '—';
  const sign = s < 0 ? '−' : '+';
  const abs = Math.abs(s);
  const m = Math.floor(abs / 60);
  const sec = Math.floor(abs % 60);
  return `${sign}${m}:${sec.toString().padStart(2, '0')}`;
}

function formatDuration(secs) {
  if (secs == null) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

// Binary search: find the resampled row where elapsed_a_s is closest to T
function binarySearchElapsed(resampled, T, key = 'elapsed_a_s') {
  let lo = 0, hi = resampled.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (resampled[mid][key] < T) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// Extract GPS coords from a chunk (supports nested points[] and flat lat/lon formats)
function chunkLatLon(chunk) {
  if (!chunk) return null;
  if (chunk.points?.length) {
    const pt = chunk.points[0];
    if (pt.lat != null && pt.lon != null) return [pt.lat, pt.lon];
  }
  if (chunk.lat != null && chunk.lon != null) return [chunk.lat, chunk.lon];
  return null;
}

// Extract all GPS coords from a workout's chunks
function buildTrack(analysis) {
  if (!analysis?.chunks) return [];
  const track = [];
  for (const chunk of analysis.chunks) {
    const pos = chunkLatLon(chunk);
    if (pos) track.push(pos);
  }
  return track;
}

// Get position at elapsed time T from analysis + resampled
function getPosAtTime(analysis, resampled, T, elapsedKey) {
  if (!resampled.length || !analysis?.chunks) return null;
  const idx = binarySearchElapsed(resampled, T, elapsedKey);
  const row = resampled[Math.min(idx, resampled.length - 1)];
  const courseDist = row.course_distance_m;
  const chunkIdx = Math.round(courseDist / 50);
  const chunks = analysis.chunks;
  const chunk = chunks[Math.min(chunkIdx, chunks.length - 1)];
  return chunkLatLon(chunk);
}

// Get GPS position for a checkpoint based on distance
function getCheckpointPos(analysis, distanceM) {
  if (!analysis?.chunks || distanceM == null) return null;
  const chunkIdx = Math.round(distanceM / 50);
  const chunks = analysis.chunks;
  const chunk = chunks[Math.min(Math.max(0, chunkIdx), chunks.length - 1)];
  return chunkLatLon(chunk);
}

// Build checkpoint markers data
function buildCheckpointMarkers(checkpoints, analysisA) {
  if (!checkpoints?.length || !analysisA?.chunks) return [];

  return checkpoints
    .map((cp) => {
      const pos = getCheckpointPos(analysisA, cp.distance_m);
      if (!pos) return null;
      return {
        name: cp.name,
        distance_m: cp.distance_m,
        position: pos,
        arrived_a_s: cp.arrived_a_s,
        arrived_b_s: cp.arrived_b_s,
        gap_at_cp_s: cp.gap_at_cp_s,
        stop_a_s: cp.stop_a_s,
        stop_b_s: cp.stop_b_s,
      };
    })
    .filter(Boolean);
}

// Component to set up custom Leaflet panes for proper z-ordering
function SetupPanes() {
  const map = useMap();

  useEffect(() => {
    if (!map.getPane('checkpointPane')) {
      const checkpointPane = map.createPane('checkpointPane');
      checkpointPane.style.zIndex = 400; // Below overlay pane (default 400) but above markers
    }
    if (!map.getPane('ghostPane')) {
      const ghostPane = map.createPane('ghostPane');
      ghostPane.style.zIndex = 650; // Above everything
    }
  }, [map]);

  return null;
}

// Inner component that imperatively updates markers (avoids re-render on every frame)
function GhostDots({ analysisA, analysisB, resampled, scrubberTime }) {
  const map = useMap();
  const markerARef = useRef(null);
  const markerBRef = useRef(null);
  const trailARef = useRef(null);
  const trailBRef = useRef(null);
  const trailAPoints = useRef([]);
  const trailBPoints = useRef([]);

  useEffect(() => {
    // Create markers on mount with custom pane for higher z-index
    markerARef.current = L.circleMarker([0, 0], {
      radius: 10,
      color: '#fff',
      fillColor: COLOR_A,
      fillOpacity: 0.9,
      weight: 2,
      pane: 'ghostPane',
    }).addTo(map);

    markerBRef.current = L.circleMarker([0, 0], {
      radius: 10,
      color: '#fff',
      fillColor: COLOR_B,
      fillOpacity: 0.9,
      weight: 2,
      pane: 'ghostPane',
    }).addTo(map);

    trailARef.current = L.polyline([], {
      color: COLOR_A,
      opacity: 0.45,
      weight: 3,
      dashArray: '4 4',
      pane: 'ghostPane',
    }).addTo(map);

    trailBRef.current = L.polyline([], {
      color: COLOR_B,
      opacity: 0.45,
      weight: 3,
      dashArray: '4 4',
      pane: 'ghostPane',
    }).addTo(map);

    return () => {
      markerARef.current?.remove();
      markerBRef.current?.remove();
      trailARef.current?.remove();
      trailBRef.current?.remove();
    };
  }, [map]);

  // Update positions imperatively on scrubberTime change
  useEffect(() => {
    const posA = getPosAtTime(analysisA, resampled, scrubberTime, 'elapsed_a_s');
    const posB = getPosAtTime(analysisB, resampled, scrubberTime, 'elapsed_b_s');

    if (posA) {
      markerARef.current?.setLatLng(posA);
      trailAPoints.current = [...trailAPoints.current.slice(-TRAIL_LEN), posA];
      trailARef.current?.setLatLngs(trailAPoints.current);
    }
    if (posB) {
      markerBRef.current?.setLatLng(posB);
      trailBPoints.current = [...trailBPoints.current.slice(-TRAIL_LEN), posB];
      trailBRef.current?.setLatLngs(trailBPoints.current);
    }
  }, [scrubberTime, analysisA, analysisB, resampled]);

  return null;
}

// Gap HUD overlay (pure HTML positioned over the map)
function GapHud({ resampled, scrubberTime }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const row = useMemo(() => {
    if (!resampled.length) return null;
    const idx = binarySearchElapsed(resampled, scrubberTime, 'elapsed_a_s');
    return resampled[Math.min(idx, resampled.length - 1)];
  }, [resampled, scrubberTime]);

  if (!row) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 1000,
        backgroundColor: 'rgba(15,23,42,0.85)',
        border: `1px solid rgba(255,255,255,0.15)`,
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        minWidth: 150,
        pointerEvents: 'none',
      }}
    >
      <Typography variant="caption" fontWeight="bold" color="#fff" display="block" mb={0.5}>
        ⏱ {formatHms(scrubberTime)}
      </Typography>
      <Typography variant="caption" color={COLOR_A} display="block">
        A  {formatKm(row.course_distance_m)}
      </Typography>
      <Typography variant="caption" color={COLOR_B} display="block">
        B  {formatKm(row.course_distance_m)}
      </Typography>
      <Box mt={0.5} pt={0.5} sx={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography variant="caption" color={row.time_delta_s >= 0 ? '#22c55e' : '#ef4444'} display="block">
          Gap  A {formatTimeDelta(-row.time_delta_s)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function GhostRaceMap({ analysisA, analysisB, resampled = [], scrubberTime, checkpoints }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const trackA = useMemo(() => buildTrack(analysisA), [analysisA]);
  const trackB = useMemo(() => buildTrack(analysisB), [analysisB]);

  // Build checkpoint markers
  const checkpointMarkers = useMemo(() => buildCheckpointMarkers(checkpoints, analysisA), [checkpoints, analysisA]);

  // Compute map center from track A
  const center = useMemo(() => {
    if (trackA.length) return trackA[Math.floor(trackA.length / 2)];
    if (trackB.length) return trackB[Math.floor(trackB.length / 2)];
    return [46.0, 7.0]; // fallback to Alps
  }, [trackA, trackB]);

  if (!trackA.length && !trackB.length) {
    return (
      <Box p={3} textAlign="center">
        <Typography color={colors.grey[500]}>{t('noGpsDataMap', 'No GPS data available for map')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: 420, borderRadius: 2, overflow: 'hidden' }}>
      <MapContainer
        center={center}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        zoomControl
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />

        {/* Track polylines */}
        {trackA.length > 0 && (
          <Polyline positions={trackA} pathOptions={{ color: COLOR_A, opacity: 0.5, weight: 2 }} />
        )}
        {trackB.length > 0 && (
          <Polyline positions={trackB} pathOptions={{ color: COLOR_B, opacity: 0.5, weight: 2, dashArray: '6 4' }} />
        )}

        {/* Set up custom panes for z-ordering */}
        <SetupPanes />

        {/* Checkpoint markers (background, lower z-index than ghost dots) */}
        {checkpointMarkers.map((cp, i) => (
          <Marker key={i} position={cp.position} icon={createCheckpointIcon()} pane="checkpointPane">
            <Popup>
              <Box sx={{ minWidth: 180 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="#8b5cf6" mb={0.5}>
                  {cp.name}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" mb={1}>
                  {formatKm(cp.distance_m)}
                </Typography>
                <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.1)', pt: 0.5, mt: 0.5 }}>
                  <Typography variant="caption" display="block" sx={{ color: COLOR_A }}>
                    A: {formatDuration(cp.arrived_a_s)}
                    {cp.stop_a_s > 10 && ` (+${formatDuration(cp.stop_a_s)} stop)`}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ color: COLOR_B }}>
                    B: {cp.arrived_b_s != null ? formatDuration(cp.arrived_b_s) : t('notDetected', '(not detected)')}
                    {cp.stop_b_s > 10 && ` (+${formatDuration(cp.stop_b_s)} stop)`}
                  </Typography>
                  {cp.gap_at_cp_s != null && (
                    <Typography variant="caption" display="block" fontWeight="bold" mt={0.5}>
                      Gap: {formatTimeDelta(cp.gap_at_cp_s)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Popup>
          </Marker>
        ))}

        {/* Animated ghost dots */}
        <GhostDots
          analysisA={analysisA}
          analysisB={analysisB}
          resampled={resampled}
          scrubberTime={scrubberTime}
        />
      </MapContainer>

      {/* HUD overlay */}
      <GapHud resampled={resampled} scrubberTime={scrubberTime} />
    </Box>
  );
}
